import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { encryptData } from 'services/authentication/encryptData';
import { useFetchData } from 'services/api/useFetchData';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import SwitchButton from 'components/buttons/SwitchButton';
import '../../../style/scss/settings.scss';

// Settings page with the security settings of the environment
const SecuritySettings: React.FC = () => {
    const { t } = useTranslation();
    const { environmentHash, setRedirectTo, handleLogout } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const history = useHistory();
    const { response: environmentSettings, loading } = useFetchData({ apiUrl: `get_environment_settings/${environmentHash}` });
    const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);

    // Set the current two factor requirement from the fetched settings
    useEffect(() => {
        if (environmentSettings) {
            setTwoFactorRequired(environmentSettings.security_two_factor_required === true);
        }
    }, [environmentSettings]);

    // Handle the toggling of the two factor requirement switch
    const handleTwoFactorToggle = async (isOn: boolean) => {
        const previousValue = twoFactorRequired;
        setTwoFactorRequired(isOn);

        try {
            // Save the new value of the setting
            await saveData({
                apiUrl: `patch_environment_settings/${environmentHash}`,
                method: 'patch',
                data: { security_two_factor_required: isOn }
            });

            // Show success alert
            setFloatingAlert({ type: 'success' });

            // Redirect the admin to the setup screen when not enrolled yet
            if (isOn) {
                const status = await fetchData({ apiUrl: 'get_two_factor_status', handleLogout });
                if (status && !status.two_factor_confirmed) {
                    setRedirectTo('setup-2fa');
                    localStorage.setItem('redirect_to', encryptData(JSON.stringify('setup-2fa')));
                    history.push('/setup-2fa');
                }
            }
        } catch (error) {
            // Revert the switch when saving failed
            console.error(error);
            setTwoFactorRequired(previousValue);
        }
    };

    return (
        <div className='container-filled'>
            {loading === 'show-loader' ? (
                <div className='inline-form-loader'>
                    <div className="loader"></div>
                </div>
            ) : loading === 'success' ? (
                <div className='section settings-form'>
                    <div className='setting-item'>
                        <div className='switch-button-wrapper'>
                            <SwitchButton
                                name='security_two_factor_required'
                                value={twoFactorRequired}
                                onToggle={handleTwoFactorToggle}
                            />
                        </div>
                        <div>
                            <div className='title switch'>
                                <h5>{t('settings.security.require_two_factor_title')}</h5>
                            </div>
                            <div className='content'>
                                <div className='col-100'>
                                    <div className='col-70'>
                                        <p className='explanation-text'>
                                            {t('settings.security.require_two_factor_explanation')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default SecuritySettings;
